import React, { useRef, useEffect, useState, useCallback, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import useStreamingChat from "../chatGPT/hooks/useStreamingChat";
import VirtualizedChatMessages from "../chatGPT/components/VirtualizedChatMessages";
import ChatHistorySidebar from "../chatGPT/components/ChatHistorySidebar";
import { useChatHistory } from "../chatGPT/hooks/useChatHistory";
import { ProductsContext } from "../context/products.context";
import {
  ArrowUp,
  ArrowDown,
  Bot,
  Square
} from "lucide-react";
import { cn } from "../utils/cn";
import { Tooltip } from "../components/ui/Tooltip";

export default function ChatHome() {
  const {
    messages: streamingMessages, // Messages for the chat being streamed
    send,
    loading,
    progressMessage,
    toolCallsInProgress,
    newChat,
    cancel,
    chatId: streamingChatId
  } = useStreamingChat();

  const { saveChat, loadChat, loadingChat } = useChatHistory();
  const {
    loadChatMessages,
    visibleChatId,
    setVisibleChatId,
    getMessagesForChat
  } = useContext(ProductsContext);

  // Get messages for the currently visible chat (may differ from streaming chat)
  const visibleMessages = getMessagesForChat(visibleChatId);

  // Use visible messages for display, but check if we're viewing the streaming chat
  const isViewingStreamingChat = visibleChatId === streamingChatId;
  const messages = isViewingStreamingChat ? streamingMessages : visibleMessages;

  const [input, setInput] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isFollowing, setIsFollowing] = useState(true);
  const [viewKey, setViewKey] = useState(0); // Increments on every "New chat" to trigger animation
  const textareaRef = useRef(null);
  const messageContainerRef = useRef(null);
  const scrollToBottomRef = useRef(() => {});
  const scrollByRef = useRef(() => {});
  const isLoadingChatRef = useRef(false);

  // Callback to receive scrollToBottom function from VirtualizedChatMessages
  const handleScrollToBottom = useCallback((fn) => {
    scrollToBottomRef.current = fn;
  }, []);

  // Callback to receive scrollBy function from VirtualizedChatMessages
  const handleScrollBy = useCallback((fn) => {
    scrollByRef.current = fn;
  }, []);

  // Auto-expand textarea as user types (up to ~350px)
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "112px"; // Base height (friendly space)
      textareaRef.current.style.height = `${Math.min(350, textareaRef.current.scrollHeight)}px`;
    }
  }, [input]);

  // Handle ESC key to cancel
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && loading) {
        cancel();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [loading, cancel]);

  // Handle submit
  const handleSend = () => {
    if (!input.trim()) return;
    send(input);
    setInput("");
  };

  // Handle copy code or text to clipboard
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  // New chat handler - increments viewKey to trigger "fresh slate" animation
  const handleNewChat = useCallback(() => {
    setInput(""); // Clear any draft
    newChat();
    setViewKey((k) => k + 1);
  }, [newChat]);

  // Focus textarea when entering empty state (after animation settles)
  useEffect(() => {
    if (messages.length === 0) {
      const id = requestAnimationFrame(() => textareaRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
  }, [messages.length, viewKey]);

  // Auto-save the streaming chat when its messages complete
  useEffect(() => {
    if (isLoadingChatRef.current) return;

    if (streamingMessages.length > 0 && streamingChatId) {
      const lastMessage = streamingMessages[streamingMessages.length - 1];
      if (lastMessage?.role === "assistant" && lastMessage?.done) {
        saveChat(streamingChatId, streamingMessages);
      }
    }
  }, [streamingMessages, streamingChatId, saveChat]);

  // Load selected chat - allows background streaming to continue
  const handleSelectChat = async (selectedChatId) => {
    // Check if we already have this chat's messages loaded
    const existingMessages = getMessagesForChat(selectedChatId);

    if (existingMessages && existingMessages.length > 0) {
      // Already in memory, just switch view
      setVisibleChatId(selectedChatId);
      return;
    }

    isLoadingChatRef.current = true;

    const chatData = await loadChat(selectedChatId);

    if (chatData) {
      if (chatData.messages && chatData.messages.length > 0) {
        const formattedMessages = chatData.messages.map((msg) => ({
          ...msg,
          done: true,
          progressMessages: msg.progressMessages || [],
          toolCalls: msg.toolCalls || 0
        }));

        // Load into the per-chat map and switch view
        loadChatMessages(selectedChatId, formattedMessages);
      } else {
        // Empty chat, just switch view
        setVisibleChatId(selectedChatId);
      }

      if (typeof window !== "undefined") {
        window.localStorage.setItem(`thread_${selectedChatId}`, selectedChatId);
      }

      setTimeout(() => {
        isLoadingChatRef.current = false;
      }, 100);
    } else {
      isLoadingChatRef.current = false;
    }
  };

  // Handle wheel event forwarding from textarea to message container
  // Uses scrollByRef to respect the child's ignore/follow logic
  // Attached via useEffect with { passive: false } so preventDefault() works
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;

    const handleWheel = (e) => {
      const isScrollable = el.scrollHeight > el.clientHeight;
      const isAtTop = el.scrollTop <= 0;
      const isAtBottom = el.scrollHeight - el.clientHeight - el.scrollTop <= 2;

      const up = e.deltaY < 0;
      const down = e.deltaY > 0;

      if (!isScrollable || (up && isAtTop) || (down && isAtBottom)) {
        e.preventDefault();
        e.stopPropagation();
        scrollByRef.current?.(e.deltaY);
      } else {
        e.stopPropagation();
      }
    };

    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, []);


  // Skeleton loading state
  const LoadingSkeleton = () => (
    <section className="flex-1 overflow-y-auto p-6 md:p-10 bg-stone-50" aria-label="Loading chat">
      <div className="max-w-3xl mx-auto space-y-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="space-y-4">
            <div className="flex justify-end">
              <div className="h-10 w-2/3 rounded-2xl bg-stone-200 animate-pulse" />
            </div>
            <div className="flex justify-start">
              <div className="space-y-2 w-full max-w-xl">
                <div className="h-4 w-full bg-stone-200 animate-pulse rounded" />
                <div className="h-4 w-5/6 bg-stone-200 animate-pulse rounded" />
                <div className="h-4 w-4/6 bg-stone-200 animate-pulse rounded" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );

  return (
    <div className="flex h-full bg-white print:h-auto print:bg-white" role="main">
      {/* Sidebar */}
      <ChatHistorySidebar
        currentChatId={visibleChatId}
        onSelectChat={handleSelectChat}
        onNewChat={handleNewChat}
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
      />

      {/* Main chat area - offset by sidebar width (collapsed: 56px, expanded: 256px) */}
      <div
        className={cn(
          "flex flex-col flex-1 transition-[margin] duration-300 ease-out print:ml-0",
          sidebarOpen ? "ml-64" : "ml-14"
        )}
      >
        {/* Chat content area */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Chat history */}
          {loadingChat ? (
            <LoadingSkeleton />
          ) : messages.length === 0 ? (
            <AnimatePresence mode="wait">
              <motion.section
                key={viewKey}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.22, ease: "easeOut" }}
                className="relative flex-1 overflow-y-auto p-6 md:p-10 print-expand bg-stone-50"
                aria-label="Chat messages"
              >
                {/* Subtle white flash overlay for "fresh slate" effect */}
                <motion.div
                  key={`flash-${viewKey}`}
                  initial={{ opacity: 0.16 }}
                  animate={{ opacity: 0 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                  className="pointer-events-none absolute inset-0 bg-white"
                />
                <div className="flex flex-col items-center pt-12 md:pt-20 text-stone-400 print:hidden">
                  <Bot size={36} className="text-stone-300 mb-4" strokeWidth={1.5} />
                  <p className="text-2xl font-medium text-stone-600">How can I help you today?</p>
                  <p className="text-sm mt-2 text-stone-400">
                    Ask about CTE programs, careers, or credentials.
                  </p>

                  {/* Input section - inline for empty state */}
                  <div className="w-full max-w-2xl mt-6">
                    <div
                      className="relative flex items-end bg-white rounded-2xl border border-stone-200 shadow-sm
                                focus-within:border-stone-300 focus-within:shadow-md transition-all outline-none"
                    >
                      <textarea
                        key={`empty-input-${viewKey}`}
                        ref={textareaRef}
                        rows={1}
                        className="flex-1 resize-none bg-transparent px-5 py-5 pr-14 min-h-[112px]
                                   focus:outline-none focus:ring-0 focus:border-transparent border-none
                                   placeholder:text-stone-400 text-stone-800"
                        placeholder="Ask a question..."
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        aria-label="Chat input"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSend();
                          }
                        }}
                        disabled={loading}
                      />
                      <div className="absolute right-3 bottom-6">
                        <button
                          onClick={handleSend}
                          disabled={!input.trim()}
                          className={cn(
                            "h-8 w-8 p-0 rounded-lg transition-colors cursor-pointer flex items-center justify-center",
                            input.trim()
                              ? "bg-violet-400 hover:bg-violet-500 text-white"
                              : "bg-violet-100 text-violet-400 hover:bg-violet-200 cursor-default"
                          )}
                          aria-label="Send message"
                        >
                          <ArrowUp size={18} strokeWidth={2.5} />
                        </button>
                      </div>
                    </div>
                  </div>

                </div>
              </motion.section>
            </AnimatePresence>
          ) : (
            <VirtualizedChatMessages
              messages={messages}
              loading={loading}
              progressMessage={progressMessage}
              toolCallsInProgress={toolCallsInProgress}
              onCopy={handleCopy}
              scrollRef={messageContainerRef}
              onFollowChange={setIsFollowing}
              onScrollToBottom={handleScrollToBottom}
              onScrollBy={handleScrollBy}
            />
          )}

          {/* Composer - fixed to bottom, only shown when there are messages */}
          {messages.length > 0 && (
            <footer
              className={cn(
                "fixed bottom-0 right-4 z-20 print:hidden pointer-events-none transition-[left] duration-300 ease-out",
                sidebarOpen ? "left-64" : "left-14"
              )}
            >
              {/* Gradient fade above */}
              <div className="absolute inset-x-0 bottom-full h-24 bg-gradient-to-t from-white to-transparent pointer-events-none" />

              {/* Jump to bottom button - attached to top of input container, above gradient */}
              {!isFollowing && messages.length > 0 && (
                <div
                  className="absolute inset-x-0 bottom-full flex justify-center"
                  style={{ marginBottom: "0.5rem" }}
                >
                  <Tooltip content="Jump to bottom" placement="top">
                    <button
                      className="pointer-events-auto w-9 h-9 rounded-full bg-white border border-stone-300
                                 flex items-center justify-center shadow-sm hover:bg-stone-50
                                 hover:border-stone-400 transition-colors cursor-pointer"
                      onClick={() => scrollToBottomRef.current(true)}
                      onWheel={(e) => {
                        // Forward wheel events to chat scroller using scrollByRef
                        e.preventDefault();
                        scrollByRef.current?.(e.deltaY);
                      }}
                      aria-label="Jump to bottom"
                    >
                      <ArrowDown size={18} className="text-stone-500" />
                    </button>
                  </Tooltip>
                </div>
              )}

              {/* White background - pointer-events-auto to prevent ghost clicks through opaque area */}
              <div
                className="bg-white px-6 pt-4 pb-6 pointer-events-auto"
                onWheel={(e) => {
                  // Forward wheel events from footer padding to chat scroller using scrollByRef
                  e.preventDefault();
                  scrollByRef.current?.(e.deltaY);
                }}
              >
                <div className="max-w-3xl mx-auto relative">
                  {/* Input container */}
                  <div
                    className="relative flex items-end bg-white rounded-2xl border border-stone-200 shadow-sm
                              focus-within:border-stone-300 focus-within:shadow-md transition-all outline-none"
                  >
                    <textarea
                      ref={textareaRef}
                      rows={1}
                      className="flex-1 resize-none bg-transparent px-5 py-5 pr-14 min-h-[112px]
                                 focus:outline-none focus:ring-0 focus:border-transparent border-none
                                 placeholder:text-stone-400 text-stone-800"
                      placeholder="Ask a question..."
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      aria-label="Chat input"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSend();
                        }
                      }}
                      disabled={loading}
                    />

                    {/* Send/Stop button - transforms based on loading state */}
                    <div className="absolute right-3 bottom-6">
                      {loading ? (
                        <Tooltip content={<>Stop response <kbd className="ml-1 text-xs opacity-70">ESC</kbd></>} placement="top">
                          <button
                            onClick={cancel}
                            className="h-8 w-8 p-0 rounded-lg cursor-pointer flex items-center justify-center
                                       bg-white hover:bg-stone-50 border border-stone-300
                                       transition-colors"
                            aria-label="Stop response"
                          >
                            <Square size={10} className="text-black fill-black" />
                          </button>
                        </Tooltip>
                      ) : (
                        <button
                          onClick={handleSend}
                          disabled={!input.trim()}
                          className={cn(
                            "h-8 w-8 p-0 rounded-lg transition-colors cursor-pointer flex items-center justify-center",
                            input.trim()
                              ? "bg-violet-400 hover:bg-violet-500 text-white"
                              : "bg-violet-100 text-violet-400 hover:bg-violet-200 cursor-default"
                          )}
                          aria-label="Send message"
                        >
                          <ArrowUp size={18} strokeWidth={2.5} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Disclaimer */}
                  <p className="mt-3 text-center text-xs text-stone-400">
                    CTE Advisor is AI and can make mistakes. Please double-check responses.
                  </p>
                </div>
              </div>
            </footer>
          )}
        </div>
      </div>
    </div>
  );
}
